import React from 'react';
import styled, { css } from 'styled-components';
import { Link, LinkProps } from 'react-router-dom';
import { colors, media, layout, remCalc } from '@prisjakt/ui-base';
import BaseCoreProperties from '@prisjakt/ui-core-properties';

interface TabListProps {
  isSticky?: boolean;
}

interface TabSubtitleProps {}

interface TabTitleProps {}

type LinkedTabProps = LinkProps & {
  active?: boolean;
  minWidth?: number;
};

export const TabList = styled.div<TabListProps>`
  background: #e4eff4;
  padding: 5px;
  display: flex;
  margin-bottom: 15px;
  transition: border-radius 0.1s linear, box-shadow 0.2s linear;

  ${props =>
    props.isSticky &&
    css`
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.1);
    `};

  ${media.largeUp`
    border-radius: ${(props: TabListProps) =>
      props.isSticky ? '0 0 10px 10px' : '10px'};
    margin-left: ${layout.pageContent.gutter.rem};
    margin-right: ${layout.pageContent.gutter.rem};

    ${(props: TabListProps) =>
      props.isSticky &&
      css`
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      `};
  `};

  ${media.mediumDown`
    padding: 0;
    box-shadow: none;
    margin-bottom: 0;
  `}

  overflow-x: auto;
`;

const LinkedTab = ({ active, ...props }: LinkedTabProps) => <Link {...props} />;

const tabTitleLineHeight = layout.lineHeight;
const tabTitleFontSize = { desktop: 14, mobile: 10, mobileLowerCase: 13 };

export const TabTitle = styled.div<TabTitleProps>`
  flex: 0 0 100%;
  font-weight: bold;
  font-size: ${remCalc(tabTitleFontSize.mobileLowerCase)};
  line-height: ${tabTitleLineHeight}em;
  opacity: 0.7;
  transition: opacity 0.1s linear;

  ${media.largeUp`
    font-size: ${remCalc(tabTitleFontSize.desktop)};
  `};
`;

const highlightedTabTitleStyle = css`
  opacity: 1;
`;

const tabSubtitleLineHeight = 1.2;
const tabSubtitleFontSize = 10;
const tabSubtitleHeight = tabSubtitleFontSize * tabSubtitleLineHeight * 2;

export const TabSubtitle = styled.div<TabSubtitleProps>`
  font-size: ${remCalc(tabSubtitleFontSize)};
  flex: 0 0 100%;
  height: ${remCalc(tabSubtitleHeight)};
  line-height: ${tabSubtitleLineHeight}em;
  opacity: 0.8;
  transition: all 0.1s linear;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const highlightedTabSubtitleStyle = css`
  opacity: 1;
`;

export const TabLowestPrice = styled(TabSubtitle)`
  font-size: 12px;
  font-weight: bold;
`;

const highlightedTabLowestPriceStyle = css`
  color: ${colors.red1};
`;

export const TabRating = TabSubtitle;

const highlightedTabRatingStyle = css`
  filter: none;
`;

export const TabSparkline = TabSubtitle;

const highlightedTabSparklineStyle = css`
  filter: none;
`;

export const Tab = styled(LinkedTab)`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  flex: 1 1 auto;
  padding: 6px 10px;
  background: ${props => (props.active ? colors.white : 'transparent')};
  border-radius: 5px;
  border-bottom: 0 none;
  text-align: center;
  color: ${props => (props.active ? colors.darkBlue1 : colors.darkBlue2)};
  cursor: ${props => (props.active ? 'default' : 'pointer')};
  hyphens: auto;
  min-width: ${props => props.minWidth || 0}px;
  :hover,
  :focus {
    ${TabTitle} {
      ${highlightedTabTitleStyle}
    }
    ${TabSubtitle} {
      ${highlightedTabSubtitleStyle}
    }
    ${TabLowestPrice} {
      ${highlightedTabLowestPriceStyle}
    }
    ${TabRating} {
      ${highlightedTabRatingStyle}
    }
    ${TabSparkline} {
      ${highlightedTabSparklineStyle}
    }
  }
  ${media.mediumDown`
    border-radius: 0;
    box-shadow: none;
    border-bottom: 1px solid ${colors.lightGray1}
  `}

  ${props =>
    props.active &&
    css`
      ${TabTitle} {
        ${highlightedTabTitleStyle}
      }
      ${TabSubtitle} {
        ${highlightedTabSubtitleStyle}
      }
      ${TabLowestPrice} {
        ${highlightedTabLowestPriceStyle}
      }
      ${TabRating} {
        ${highlightedTabRatingStyle}
      }
      ${TabSparkline} {
        ${highlightedTabSparklineStyle}
      }
    `}
  ${media.mediumDown`
      border-top: 2px solid ${colors.lightBlue3}
    `}



  ${media.largeUp`
    flex: 1 0 0;
  `};
`;

export const TabContent = styled.div`
  margin: 0 ${layout.pageContent.gutter.rem};
`;

export const StyledCoreProperties = styled(BaseCoreProperties)`
  && {
    color: inherit;
    margin: 0;
  }
`;
